import type { ErrorLike } from '@apollo/client';
import { ReactNode, createContext, useContext, useState } from 'react';

import { BundleBasketFragment } from '@generated/gql/graphql';
import { useBundleBasket } from '@hooks/useBundleBasket';
import { useSnackbarErrors } from '@hooks/useSnackbarErrors';
import { BundleBasketData } from '@src/types/bundle';
import { EnergyDetailsFormValues } from '@src/types/energy';

import { EnergyJourneyPaths, signupEnergyPaths } from './journeyPaths';
import { useEnergyDetails } from './useEnergyDetails';
import { getSnackbarErrors } from './utils';

interface EnergyContextState {
  basket: Nullable<BundleBasketData>;
  basketRaw: Nullable<BundleBasketFragment>;
  basketError?: ErrorLike;
  isBasketLoading: boolean;
  energyDetailsErrors: ErrorLike[];
  journeyPaths: EnergyJourneyPaths;
  handleEnergyDetailsSubmit: (values: EnergyDetailsFormValues) => void;
  isDetailsFormChanged: boolean;
  isDetailsLoading: boolean;
  nonEnergyServiceCount: number;
  setDetailsFormChanged: (changed: boolean) => void;
  resetDetailsApiErrors: () => void;
}

const EnergyContext = createContext<Nullable<EnergyContextState>>(null);

const useEnergyContext = () => {
  const context = useContext(EnergyContext);
  if (!context) {
    throw new Error('useEnergyContext must be used within an EnergyProvider');
  }
  return context;
};

interface EnergyProviderProps {
  children: ReactNode;
  journeyPaths?: EnergyJourneyPaths;
}

const EnergyProvider = ({
  children,
  journeyPaths = signupEnergyPaths,
}: EnergyProviderProps) => {
  const [isDetailsFormChanged, setDetailsFormChanged] = useState(false);

  const {
    basket,
    basketRaw,
    error: basketError,
    loading: isBasketLoading,
    nonEnergyServiceCount,
  } = useBundleBasket();

  const {
    handleSubmit: handleEnergyDetailsSubmit,
    isLoading: isDetailsLoading,
    errors: energyDetailsErrors,
    resetApiErrors: resetDetailsApiErrors,
  } = useEnergyDetails(basketRaw ?? null, isDetailsFormChanged, journeyPaths);

  useSnackbarErrors(
    getSnackbarErrors(energyDetailsErrors),
    resetDetailsApiErrors
  );

  const state: EnergyContextState = {
    basket: basket ?? null,
    basketRaw: basketRaw ?? null,
    basketError,
    isBasketLoading,
    energyDetailsErrors,
    handleEnergyDetailsSubmit,
    journeyPaths,
    isDetailsFormChanged,
    isDetailsLoading,
    nonEnergyServiceCount,
    setDetailsFormChanged,
    resetDetailsApiErrors,
  };

  return <EnergyContext value={state}>{children}</EnergyContext>;
};

export { EnergyProvider, useEnergyContext, EnergyContext };
export type { EnergyContextState, EnergyProviderProps };
